import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import { fetchMeals } from '../data/api';
import { SettingsModal } from './SettingsModal';
import { LoginPage } from './LoginPage';
import HelpModal from './ui/HelpModal';

// Import our new UI components
import MealFilters from './ui/MealFilters';
import MealSection from './ui/MealSection';
import LikedMealsPanel from './ui/LikedMealsPanel';
import MobileView from './ui/MobileView';

// Define PROTEIN_OPTIONS constant (moved from MealFilters)
const PROTEIN_OPTIONS = [
  { id: 'chicken', label: 'Chicken' },
  { id: 'beef', label: 'Beef' },
  { id: 'pork', label: 'Pork' },
  { id: 'turkey', label: 'Turkey' },
  { id: 'shrimp', label: 'Shrimp' },
  { id: 'white fish', label: 'White Fish' },
  { id: 'meatless', label: 'Meatless' },
  { id: 'salmon', label: 'Salmon' },
  { id: 'tuna', label: 'Tuna' },
];

// const SEASONS = ['spring/summer', 'fall/winter', 'classic', 'essential'];

// Remove email from DUMMY_USER
const DUMMY_USER = {
  password: process.env.REACT_APP_CLIENT_PASSWORD,
};

const MealPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // Remove email state
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('meals');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState(['breakfast']);
  // Near the top where states are defined, update the proteinFilters initial state
  const [proteinFilters, setProteinFilters] = useState(PROTEIN_OPTIONS.map(option => option.id));
  const [likedMeals, setLikedMeals] = useState(() => {
    const saved = localStorage.getItem('likedMeals');
    return saved ? JSON.parse(saved) : [];
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mealData, setMealData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [menuTypeFilters, setMenuTypeFilters] = useState(['classic', 'essential', 'seasonal']);
  const [maxPrepTime, setMaxPrepTime] = useState(90); // Default to 90 minutes

  const [userInfo, setUserInfo] = useState(() => {
    const savedData = JSON.parse(localStorage.getItem('userData') || '{}');
    return {
      homeId: savedData.homeId,
      contactEmail: savedData.contactEmail,
      phoneNumber: savedData.phoneNumber || '',
      householdSize: savedData.householdSize || 1,
      dietaryRestrictions: savedData.dietaryRestrictions || {}
    };
  });

  // Add this useEffect to save liked meals whenever they change
  useEffect(() => {
    localStorage.setItem('likedMeals', JSON.stringify(likedMeals));
  }, [likedMeals]);

  const handleMenuTypeFilterChange = (menuType) => {
    setMenuTypeFilters(prev => {
      if (prev.includes(menuType)) {
        // Don't remove if it's the last filter
        if (prev.length === 1) return prev;
        return prev.filter(type => type !== menuType);
      }
      return [...prev, menuType];
    });
  };

  const parsePrepTime = (prepTimeStr) => {
    if (!prepTimeStr) return 0;
    const match = prepTimeStr.match(/(\d+)/);
    return match ? parseInt(match[0]) : 0;
  };

  // Add this new function after the handleProteinFilterChange function
  const handleToggleAllProteins = () => {
    if (proteinFilters.length === PROTEIN_OPTIONS.length) {
      setProteinFilters([]); // Deselect all
    } else {
      setProteinFilters(PROTEIN_OPTIONS.map(option => option.id)); // Select all
    }
  };

  const filterMeals = (meals) => {
    const restrictions = userInfo.dietaryRestrictions || {};

    // First, group meals by their base name (removing suffixes like PF, GF, etc.)
    const mealGroups = meals.reduce((groups, meal) => {
      // Remove all known suffixes to get base name
      const baseName = meal.name.replace(/ (PF|GF|DF|NF|EF|SF|SSF|SFF|CF)(\s|$)/g, '');
      if (!groups[baseName]) {
        groups[baseName] = [];
      }
      groups[baseName].push(meal);
      return groups;
    }, {});

    // Then filter meals based on restrictions
    return Object.values(mealGroups).flatMap(mealVariants => {
      // Find the variant that best matches the user's restrictions
      const bestMatch = mealVariants.find(meal => {
        const mealName = meal.name.toLowerCase();
        // Check if this variant matches user's restrictions
        const isPeanutFreeVariant = mealName.includes('pf') && restrictions.peanuts;
        const isGlutenFreeVariant = mealName.includes('gf') && restrictions.gluten;
        const isDairyFreeVariant = mealName.includes('df') && restrictions.dairy;
        const isNutFreeVariant = mealName.includes('nf') && restrictions.nuts;
        const isEggFreeVariant = mealName.includes('ef') && restrictions.eggs;
        const isSoyFreeVariant = mealName.includes('sf') && restrictions.soy;
        const isSesameFreeVariant = mealName.includes('ssf') && restrictions.sesame;
        const isShellfishFreeVariant = mealName.includes('sff') && restrictions.shellfish;
        const isCoconutFreeVariant = mealName.includes('cf') && restrictions.coconut;

        // Return true if this variant matches any of the user's restrictions
        return isPeanutFreeVariant || isGlutenFreeVariant || isDairyFreeVariant || 
               isNutFreeVariant || isEggFreeVariant || isSoyFreeVariant || 
               isSesameFreeVariant || isShellfishFreeVariant || isCoconutFreeVariant;
      }) || mealVariants[0]; // If no variant matches restrictions, use the default version

      // Only return the best matching variant
      return [bestMatch];
    }).filter(meal => {
      // Apply standard dietary restriction checks
      if (restrictions.gluten && meal.Gluten === 'yes') return false;
      if (restrictions.dairy && meal.Dairy === 'yes') return false;
      if (restrictions.peanuts && meal.Peanuts === 'yes') return false;
      if (restrictions.nuts && (meal.Almonds === 'yes' || meal['Tree Nuts'] === 'yes' || meal.Cashews === 'yes')) return false;
      if (restrictions.eggs && meal.Eggs === 'yes') return false;
      if (restrictions.soy && meal.Soy === 'yes') return false;
      if (restrictions.sesame && meal.Sesame === 'yes') return false;
      if (restrictions.shellfish && meal.Shellfish === 'yes') return false;
      if (restrictions.coconut && meal.Coconut === 'yes') return false;

      // Check prep time
      const mealPrepTime = parsePrepTime(meal.prepTime);
      if (mealPrepTime > maxPrepTime) return false;
      
      // Check dietary limits
      const limits = userInfo.dietaryLimits || {};
      if (restrictions.lowSodium && limits.sodium && parseInt(meal.sodium) > parseInt(limits.sodium)) return false;
      if (restrictions.lowCarb && limits.carbs && meal.carbs > parseInt(limits.carbs)) return false;
      if (restrictions.lowCalorie && limits.calories && meal.calories > parseInt(limits.calories)) return false;
      
      // Check menu type filters
      const mealSeason = meal.season?.toLowerCase() || '';
      const currentSeason = getCurrentSeason();
      
      // Handle menu type filtering
      if (!menuTypeFilters.some(filter => {
        if (filter === 'seasonal') {
          return mealSeason === currentSeason.toLowerCase();
        }
        return mealSeason === filter;
      })) {
        return false;
      }
  
      // Hide gluten-free variants if gluten-free is not restricted
      if (!restrictions.gluten) {
        if (meal.name.toLowerCase().includes('gf') || 
            meal.name.toLowerCase().includes('gluten free') ||
            meal.name.toLowerCase().includes('gluten-free')) {
          return false;
        }
      }
  
      // Hide dairy-free variants if dairy-free is not restricted
      if (!restrictions.dairy) {
        if (meal.name.toLowerCase().includes('dairy free') ||
            meal.name.toLowerCase().includes('dairy-free') ||
            meal.name.toLowerCase().includes('df')) {
          return false;
        }
      }
      
      return true;
    });
  };
  
  const getCurrentSeason = () => {
    const month = new Date().getMonth() + 1; // getMonth() returns 0-11
    // March (3) through September (9) is spring/summer
    return (month >= 3 && month <= 9) ? 'Spring/Summer' : 'Fall/Winter';
  };

  const handleProteinFilterChange = (proteinId) => {
    setProteinFilters((prev) => {
      if (prev.includes(proteinId)) {
        return prev.filter((id) => id !== proteinId);
      }
      return [...prev, proteinId];
    });
  };

  const toggleSection = (type) => {
    setExpandedSections(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      }
      return [...prev, type];
    });
  };

  const scrollToMeal = (mealId) => {
    const mealElement = document.getElementById(mealId);
    if (mealElement) {
      mealElement.scrollIntoView({ behavior: 'smooth' });
      mealElement.classList.add('highlight-meal');
      setTimeout(() => {
        mealElement.classList.remove('highlight-meal');
      }, 2000);
    }
  };

  const handleLikeMeal = (meal, resetAll = false) => {
    if (resetAll) {
      setLikedMeals([]);
      localStorage.setItem('likedMeals', JSON.stringify([]));
      return;
    }
    
    // Don't scroll or navigate when liking/unliking a meal
    setLikedMeals(prev => {
      const newMeals = prev.some(m => m.id === meal.id)
        ? prev.filter(m => m.id !== meal.id)
        : [...prev, meal];
      localStorage.setItem('likedMeals', JSON.stringify(newMeals));
      return newMeals;
    });
  };

  const handleImportCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;
  
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        try {
          if (!results.data || !results.data.length) {
            setError('Invalid file format');
            return;
          }

          const importedMeals = results.data
            .filter(row => row.ID)
            .map(row => {
              return Object.values(mealData)
                .flat()
                .find(meal => meal.id === row.ID);
            })
            .filter(meal => meal); // Remove any undefined meals
  
        setLikedMeals(prev => {
          const newMeals = [...prev];
          importedMeals.forEach(meal => {
            if (!newMeals.some(m => m.id === meal.id)) {
              newMeals.push(meal);
            }
          });
          return newMeals;
        });
        setSuccess('Meals imported successfully');
      } catch (error) {
        setError('Error importing meals');
      }
    }});
  };

  const handleSignOut = () => {
    setIsLoggedIn(false);
    setPassword('');
  };

  const handleSettingsUpdate = (data) => {
      setUserInfo(prev => ({
        ...prev,
        phoneNumber: data.phoneNumber,
        householdSize: data.householdSize,
        dietaryRestrictions: data.dietaryRestrictions,
        dietaryLimits: data.dietaryLimits
      }));
  
      // Update localStorage
      localStorage.setItem('userData', JSON.stringify(data));
    };

  const fetchMealData = async () => {
    try {
      const data = await fetchMeals();
      setMealData(data);
    } catch (error) {
      console.error('Error fetching meal data:', error);
      setError('Failed to load meal data');
    }
  };

  useEffect(() => {
    fetchMealData();
  }, []);

  useEffect(() => {
    if (activeTab === 'settings') {
      setShowSettingsModal(true);
      setActiveTab('meals');
    }
  }, [activeTab]);

  // Handle submit meals function
  const handleSubmitMeals = async () => {
    const breakfastCount = likedMeals.filter(meal => meal.type === 'breakfast').length;
    const lunchCount = likedMeals.filter(meal => meal.type === 'lunch').length;
    const dinnerCount = likedMeals.filter(meal => meal.type === 'dinner').length;

    if (breakfastCount < 8 || lunchCount < 8 || dinnerCount < 14) {
      alert(`Please select more than 8 meals for breakfast, 8 meals for lunch, and 14 meals for dinner:\nBreakfast: ${breakfastCount}\nLunch: ${lunchCount}\nDinner: ${dinnerCount}`);
      return;
    }

    const formattedData = {
      userInfo: {
        date: new Intl.DateTimeFormat('en-US', {
          dateStyle: 'short',
          timeStyle: 'medium'
        }).format(new Date()),
        ...JSON.parse(localStorage.getItem('userData') || '{}'),
        homeId: JSON.parse(localStorage.getItem('userData') || '{}').homeId || userInfo.homeId,
        contactEmail: JSON.parse(localStorage.getItem('userData') || '{}').contactEmail || userInfo.contactEmail,
        phoneNumber: userInfo.phoneNumber,
        householdSize: userInfo.householdSize,
        dietaryRestrictions: Object.entries(userInfo.dietaryRestrictions)
          .filter(([_, value]) => value)
          .map(([key]) => {
            if (key === 'lowCarb' && userInfo.dietaryLimits?.carbs) {
              return `Carb Limit = ${userInfo.dietaryLimits.carbs}g`;
            }
            if (key === 'lowSodium' && userInfo.dietaryLimits?.sodium) {
              return `Sodium Limit = ${userInfo.dietaryLimits.sodium}mg`;
            }
            if (key === 'lowCalorie' && userInfo.dietaryLimits?.calories) {
              return `Calorie Limit = ${userInfo.dietaryLimits.calories}cal`;
            }
            return key;
          })
      },
      selectedBreakfast: likedMeals
        .filter(meal => meal.type === 'breakfast')
        .map(meal => meal.id),
      selectedLunch: likedMeals
        .filter(meal => meal.type === 'lunch')
        .map(meal => meal.id),
      selectedDinner: likedMeals
        .filter(meal => meal.type === 'dinner')
        .map(meal => meal.id),
    };

    try {
      const response = await fetch('https://hook.us2.make.com/fqyw4ogwiy7ldcr9fkt6a731mgw8p71j', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData)
      });
      
      if (!response.ok) throw new Error('Failed to send data');
      alert('Meals sent successfully!');
    } catch (error) {
      console.error('Error sending data:', error);
      alert('Failed to send meals. Please try again later.');
    }
  };

  // Add toggleHelpModal function
  const toggleHelpModal = () => {
    setShowHelpModal(!showHelpModal);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {!isLoggedIn ? (
        <LoginPage onLogin={(password) => {
          if (password === DUMMY_USER.password) {
            setIsLoggedIn(true);
            setShowSettingsModal(true);
          }
        }} />
      ) : (
        <>
          <SettingsModal
            isOpen={showSettingsModal}
            onClose={() => setShowSettingsModal(false)}
            userInfo={userInfo}
            onUpdate={handleSettingsUpdate}
            onSignOut={handleSignOut}
          />
          
          <HelpModal isOpen={showHelpModal} onClose={toggleHelpModal} />

          <nav className={`${
            getCurrentSeason() === 'Spring/Summer' 
              ? "bg-gradient-to-r from-pink-100/90 via-green-100/90 to-yellow-100/90"
              : "bg-gradient-to-r from-amber-100/90 via-orange-100/90 to-red-100/90"
          } backdrop-blur-md shadow-sm lg:sticky lg:top-0 lg:z-10`}>
            <div className="max-w-[90rem] mx-auto px-2 sm:px-4 lg:px-8">
              <div className="flex flex-col sm:flex-row justify-between py-2 sm:py-4 sm:h-16">
                <div className="flex flex-col sm:flex-row items-center mb-1 sm:mb-0">
                  <div className="flex-shrink-0 flex flex-col items-center mb-1 sm:mb-0">
                    <h1 className="text-base sm:text-lg lg:text-xl font-bold">Whole Nutrition Services</h1>
                    <span className="text-xs text-emerald-700 -mt-1">{getCurrentSeason()}</span>
                  </div>
                  <div className="hidden lg:flex space-x-4 sm:ml-6 sm:space-x-8">
                    <button className="inline-flex items-center px-1 pt-1 border-b-2 border-blue-500 text-sm sm:text-base">
                      Meals
                    </button>
                    <button
                      onClick={() => setShowSettingsModal(true)}
                      className="inline-flex items-center px-1 pt-1 hover:border-b-2 hover:border-blue-500 transition-colors text-sm sm:text-base"
                    >
                      Update Info
                    </button>
                    <button
                      onClick={toggleHelpModal}
                      className="inline-flex items-center px-1 pt-1 hover:border-b-2 hover:border-yellow-500 transition-colors text-sm sm:text-base"
                    >
                      Help
                    </button>
                  </div>
                </div>
                <div className="hidden lg:flex items-center justify-center space-x-4">
                  <button
                    onClick={handleSignOut}
                    className="text-red-600 hover:text-red-700 transition-colors text-sm sm:text-base"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </nav>

          {/* Active Dietary Restrictions Banner */}
          {Object.entries(userInfo.dietaryRestrictions || {}).filter(([_, value]) => value).length > 0 && (
            <div className="bg-blue-50 border-y border-blue-200">
              <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-3">
                <p className="text-blue-800 font-medium">
                  Active Dietary Restrictions: {Object.entries(userInfo.dietaryRestrictions || {})
                    .filter(([_, value]) => value)
                    .map(([key]) => {
                      if (key === 'lowCarb' && userInfo.dietaryLimits?.carbs) {
                        return `Carb Limit = ${userInfo.dietaryLimits.carbs}g`;
                      }
                      if (key === 'lowSodium' && userInfo.dietaryLimits?.sodium) {
                        return `Sodium Limit = ${userInfo.dietaryLimits.sodium}mg`;
                      }
                      if (key === 'lowCalorie' && userInfo.dietaryLimits?.calories) {
                        return `Calorie Limit = ${userInfo.dietaryLimits.calories}cal`;
                      }
                      const label = key === 'treeNuts' ? 'Tree Nuts' :
                                  key === 'gluten' ? 'Gluten' :
                                  key === 'dairy' ? 'Dairy' :
                                  key === 'peanuts' ? 'Peanuts' :
                                  key === 'soy' ? 'Soy' :
                                  key === 'shellfish' ? 'Shellfish' :
                                  key === 'eggs' ? 'Eggs' :
                                  key === 'sesame' ? 'Sesame' : key;
                                  // key === 'vegetarian' ? 'Vegetarian' :
                                  // key === 'vegan' ? 'Vegan' : key;
                      return label;
                    }).join(', ')}
                </p>
              </div>
            </div>
          )}

          <main className="max-w-[90rem] mx-auto py-6 px-4 sm:px-6 lg:px-8">
            {activeTab === 'meals' ? (
              <>
                {/* Mobile View */}
                <MobileView
                  mealData={mealData}
                  filterMeals={filterMeals}
                  likedMeals={likedMeals}
                  handleLikeMeal={handleLikeMeal}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  proteinFilters={proteinFilters}
                  handleProteinFilterChange={handleProteinFilterChange}
                  handleToggleAllProteins={handleToggleAllProteins}
                  maxPrepTime={maxPrepTime}
                  setMaxPrepTime={setMaxPrepTime}
                  menuTypeFilters={menuTypeFilters}
                  handleMenuTypeFilterChange={handleMenuTypeFilterChange}
                  handleSubmitMeals={handleSubmitMeals}
                  showSettingsModal={showSettingsModal}
                  setShowSettingsModal={setShowSettingsModal}
                  userInfo={userInfo}
                  handleSettingsUpdate={handleSettingsUpdate}
                  onSignOut={handleSignOut}
                />

                {/* Desktop View */}
                <div className="hidden lg:grid lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <div className="mb-8">
                      <h2 className="text-3xl font-bold mb-6">Select Meals You Would Like</h2>
                      
                      {/* Filter Section */}
                      <MealFilters
                        expandedSections={expandedSections}
                        toggleSection={toggleSection}
                        proteinFilters={proteinFilters}
                        handleProteinFilterChange={handleProteinFilterChange}
                        handleToggleAllProteins={handleToggleAllProteins}
                        maxPrepTime={maxPrepTime}
                        setMaxPrepTime={setMaxPrepTime}
                        menuTypeFilters={menuTypeFilters}
                        handleMenuTypeFilterChange={handleMenuTypeFilterChange}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                      />
                    </div>

                    {/* Meal Sections */}
                    {['breakfast', 'lunch', 'dinner'].map(type => (
                      <MealSection
                        key={type}
                        type={type}
                        meals={filterMeals(mealData[type] || [])}
                        expandedSections={expandedSections}
                        toggleSection={toggleSection}
                        likedMeals={likedMeals}
                        onLikeMeal={handleLikeMeal}
                      />
                    ))}
                  </div>

                  {/* Liked Meals Panel */}
                  <LikedMealsPanel
                    likedMeals={likedMeals}
                    handleLikeMeal={handleLikeMeal}
                    expandedSections={expandedSections}
                    toggleSection={toggleSection}
                    scrollToMeal={scrollToMeal}
                    onClearLiked={() => setLikedMeals([])}
                    onSubmitMeals={handleSubmitMeals}
                  />
                </div>
              </>
            ) : (
              <div className="bg-white shadow rounded-lg">
                <div className="p-6">
                  <h2 className="text-2xl font-bold mb-6">Account Settings</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-medium mb-2">Email</h3>
                      <p className="text-gray-600">{userInfo.contactEmail}</p>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">Dietary Restrictions</h3>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(userInfo.dietaryRestrictions || {})
                          .filter(([_, value]) => value)
                          .map(([key]) => (
                            <span
                              key={key}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                            >
                              {key}
                            </span>
                          ))}
                      </div>
                    </div>
                    <div className="pt-4 border-t">
                      <button
                        onClick={handleSignOut}
                        className="w-full bg-red-600 text-white rounded-md py-2 px-4 hover:bg-red-700 transition-colors"
                      >
                        Log Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </main>
        </>
      )}
    </div>
  );
}

export default MealPortal;